//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState } from 'react';

// *** MUI / STYLING ***
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Link,
    Stack,
} from '@mui/material';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// === API ===
import { workflowMonitorBulkUpdate } from 'api/prokey/prokey.websocket';
import { parseWorkflowBatchCsv } from '../workflowBatchCsv';


//#endregion

//-----------------------------------------------------------------
export function BulkUpdateDialog({ is_open, onClose, onSuccess }) {
    //-----------------------------------------------------------------

    //#region [CONSTANTS]
    const BULK_SAMPLE_CSV_URL = 'workflow-bulk-sample.csv';
    //#endregion

    //#region [STATE]
    const [bulk_file, setBulkFile] = useState(null);
    const [bulk_error, setBulkError] = useState(null);
    const [is_bulk_uploading, setIsBulkUploading] = useState(false);
    //#endregion

    //#region [STYLES]
    const _styles = {
        bulk_dialog_file_name: {
            marginLeft: '1rem',
            fontSize: '0.875rem',
        },
        bulk_dialog_error: {
            marginTop: '0.5rem',
        },
        bulk_dialog_sample_link: {
            marginTop: '0.75rem',
            display: 'inline-block',
            cursor: 'pointer',
        },
    }
    //#endregion

    //-----------------------------------------------------------------
    function onClose_BulkUpdate() {
        //-----------------------------------------------------------------
        onClose && onClose();
        setBulkFile(null);
        setBulkError(null);
        setIsBulkUploading(false);
    }

    //-----------------------------------------------------------------
    function onClick_BulkFileChoose() {
        //-----------------------------------------------------------------
        const input = document.getElementById('workflow_bulk_update_file');
        if (input) {
            input.value = '';
            input.click();
        }
    }

    //-----------------------------------------------------------------
    function onChange_BulkFile(event) {
        //-----------------------------------------------------------------
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        setBulkFile(file);
        setBulkError(null);
    }

    //-----------------------------------------------------------------
    async function onClick_BulkUpload() {
        //-----------------------------------------------------------------
        if (!bulk_file) return;

        try {
            setIsBulkUploading(true);
            setBulkError(null);

            const text = await bulk_file.text();
            const data = parseWorkflowBatchCsv(text);

            if (!data || data.batchesToUpdate.length === 0) {
                throw new Error('No rows parsed from CSV.');
            }

            workflowMonitorBulkUpdate(data);

            if (onSuccess) {
                onSuccess();
            }

            onClose_BulkUpdate();
        } catch (error) {
            console.log(error);
            setBulkError(error.message || 'Unable to read or parse file as CSV.');
            setIsBulkUploading(false);
        }
    }

    //#region [RENDER]
    //-----------------------------------------------------------------
    function display() {
        //-----------------------------------------------------------------
        return (
            <Dialog
                open={is_open}
                onClose={onClose_BulkUpdate}
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle>Bulk Update Batches</DialogTitle>
                <DialogContent dividers>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Select a CSV file containing bulk workflow update data."
                            />
                        </ListItem>

                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="You can prepare this file in one of two ways:"
                                secondary={
                                    <>
                                        <ul>
                                            <li>Use the sample CSV as a template for your data.</li>
                                            <li>Download the batch data CSV, update the priority values, and upload the file here.</li>
                                        </ul>
                                    </>
                                }
                            />
                        </ListItem>

                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="The CSV must include the following columns (case-insensitive):"
                                secondary={
                                    <>
                                        <strong>BatchName</strong> or <strong>name</strong>: The name of the batch to update.
                                        <br />
                                        <strong>UpdateValue</strong> or <strong>priority</strong>: The value used to update the batch:
                                        <br />
                                        -1: Delete batch, 0: Suspend batch, &gt;0: Update priority
                                    </>
                                }
                            />
                        </ListItem>

                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Any additional columns in the CSV will be ignored."
                            />
                        </ListItem>
                    </List>
                    <Link
                        href={BULK_SAMPLE_CSV_URL}
                        download
                        sx={_styles.bulk_dialog_sample_link}
                    >
                        Download sample CSV
                    </Link>
                    <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                        <Button
                            variant='outlined'
                            onClick={onClick_BulkFileChoose}
                        >
                            Choose File
                        </Button>
                        <Typography sx={_styles.bulk_dialog_file_name}>
                            {bulk_file ? bulk_file.name : 'No file selected'}
                        </Typography>
                    </Stack>
                    {bulk_error && (
                        <Typography sx={_styles.bulk_dialog_error} color='error' variant='body2'>
                            {bulk_error}
                        </Typography>
                    )}
                    <input
                        type='file'
                        id='workflow_bulk_update_file'
                        style={{ display: 'none' }}
                        accept='text/csv,.csv'
                        onChange={onChange_BulkFile}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose_BulkUpdate} disabled={is_bulk_uploading}>
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        onClick={onClick_BulkUpload}
                        disabled={!bulk_file || is_bulk_uploading}
                    >
                        {is_bulk_uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    //#endregion

    return display();
}
